import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";
import { RotateCcw, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { InvoiceLayoutCheck } from "@/hooks/use-invoice-layout-check";
import InvoiceLayoutWarnings from "./invoice-layout-warnings";
import InvoiceLayoutTemplates from "./invoice-layout-templates";
import {
  INVOICE_BLOCKS,
  type InvoiceBlockId,
  type InvoiceLayout,
} from "@shared/invoice-layout";

type InvoiceLayoutEditorProps = {
  layout: InvoiceLayout | undefined;
  onChange: (layout: InvoiceLayout) => void;
  layoutCheck?: InvoiceLayoutCheck;
};

const MIN_X = -180;
const MAX_X = 180;
const MIN_Y = -250;
const MAX_Y = 250;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export default function InvoiceLayoutEditor({
  layout,
  onChange,
  layoutCheck,
}: InvoiceLayoutEditorProps) {
  const [selectedId, setSelectedId] = useState<InvoiceBlockId>(INVOICE_BLOCKS[0].id);
  const [xDraft, setXDraft] = useState("0");
  const [yDraft, setYDraft] = useState("0");
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    blockId: InvoiceBlockId;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);

  const selectedBlock = INVOICE_BLOCKS.find((block) => block.id === selectedId) ?? INVOICE_BLOCKS[0];
  const selectedOffset = layout?.[selectedId] ?? { x: 0, y: 0 };

  useEffect(() => {
    setXDraft(String(selectedOffset.x));
    setYDraft(String(selectedOffset.y));
  }, [selectedId, selectedOffset.x, selectedOffset.y]);

  const updateOffset = useCallback((
    id: InvoiceBlockId,
    x: number,
    y: number,
  ) => {
    onChange({
      ...layout,
      [id]: {
        x: clamp(Number.isFinite(x) ? x : 0, MIN_X, MAX_X),
        y: clamp(Number.isFinite(y) ? y : 0, MIN_Y, MAX_Y),
      },
    });
  }, [layout, onChange]);

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>, id: InvoiceBlockId) => {
    if (event.button !== 0) return;
    const sheet = sheetRef.current;
    if (!sheet) return;

    const rect = sheet.getBoundingClientRect();
    const offset = layout?.[id] ?? { x: 0, y: 0 };
    dragRef.current = {
      pointerId: event.pointerId,
      blockId: id,
      startX: ((event.clientX - rect.left) / rect.width) * 210,
      startY: ((event.clientY - rect.top) / rect.height) * 297,
      startOffsetX: offset.x,
      startOffsetY: offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedId(id);
    event.preventDefault();
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const sheet = sheetRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !sheet) return;

    const rect = sheet.getBoundingClientRect();
    const pointX = ((event.clientX - rect.left) / rect.width) * 210;
    const pointY = ((event.clientY - rect.top) / rect.height) * 297;
    updateOffset(
      drag.blockId,
      Math.round((drag.startOffsetX + pointX - drag.startX) * 10) / 10,
      Math.round((drag.startOffsetY + pointY - drag.startY) * 10) / 10,
    );
    event.preventDefault();
  };

  const finishDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const moveSelectedBy = (dx: number, dy: number) => {
    const current = layout?.[selectedId] ?? { x: 0, y: 0 };
    updateOffset(selectedId, current.x + dx, current.y + dy);
  };

  const resetSelected = () => updateOffset(selectedId, 0, 0);
  const resetAll = () => onChange({});

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-bauwerk-teal">
          <Move className="h-4 w-4" aria-hidden="true" />
          Deckblatt-Layout
        </CardTitle>
        <p className="text-sm leading-relaxed text-slate-600">
          Verschieben Sie einzelne Bereiche des Deckblatts. Die Werte sind Versätze in Millimetern
          zur automatischen PDF-Position.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <InvoiceLayoutTemplates layout={layout} onChange={onChange} />
        <InvoiceLayoutWarnings check={layoutCheck} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_15rem]">
          <div className="min-w-0">
            <div
              ref={sheetRef}
              className="relative mx-auto w-full max-w-[25rem] overflow-hidden rounded-sm border border-slate-300 bg-[#fffefa] shadow-sm"
              style={{ aspectRatio: "210 / 297" }}
              role="group"
              aria-label="Schematische A4-Seite. Bereiche mit Maus oder Tastatur verschieben."
            >
              <div className="pointer-events-none absolute inset-[4.2%] border border-dashed border-slate-200" />
              {INVOICE_BLOCKS.map((block, index) => {
                const offset = layout?.[block.id] ?? { x: 0, y: 0 };
                const isSelected = selectedId === block.id;
                return (
                  <button
                    key={block.id}
                    type="button"
                    aria-label={`${block.label}, Versatz X ${offset.x} Millimeter, Y ${offset.y} Millimeter. Mit Pfeiltasten verschieben.`}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedId(block.id)}
                    onPointerDown={(event) => handlePointerDown(event, block.id)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={finishDrag}
                    onPointerCancel={finishDrag}
                    onKeyDown={(event) => {
                      const step = event.shiftKey ? 5 : 1;
                      if (event.key === "ArrowLeft") {
                        event.preventDefault();
                        setSelectedId(block.id);
                        const current = layout?.[block.id] ?? { x: 0, y: 0 };
                        updateOffset(block.id, current.x - step, current.y);
                      } else if (event.key === "ArrowRight") {
                        event.preventDefault();
                        setSelectedId(block.id);
                        const current = layout?.[block.id] ?? { x: 0, y: 0 };
                        updateOffset(block.id, current.x + step, current.y);
                      } else if (event.key === "ArrowUp") {
                        event.preventDefault();
                        setSelectedId(block.id);
                        const current = layout?.[block.id] ?? { x: 0, y: 0 };
                        updateOffset(block.id, current.x, current.y - step);
                      } else if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setSelectedId(block.id);
                        const current = layout?.[block.id] ?? { x: 0, y: 0 };
                        updateOffset(block.id, current.x, current.y + step);
                      }
                    }}
                    className={`absolute flex min-h-0 items-center justify-center overflow-hidden border px-1 text-center text-[clamp(6px,1.7vw,10px)] leading-tight transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal ${
                      isSelected
                        ? "z-[1] border-bauwerk-teal bg-bauwerk-teal/15 font-semibold text-bauwerk-teal"
                        : "border-slate-300 bg-slate-100/80 text-slate-600 hover:border-bauwerk-teal/70 hover:bg-bauwerk-teal/10"
                    }`}
                    style={{
                      left: `${((block.x + offset.x) / 210) * 100}%`,
                      top: `${((block.y + offset.y) / 297) * 100}%`,
                      width: `${(block.width / 210) * 100}%`,
                      height: `${Math.max(block.height, 6) / 297 * 100}%`,
                      touchAction: "none",
                    }}
                  >
                    <span className="pointer-events-none line-clamp-2">
                      {index + 1}. {block.label}
                    </span>
                  </button>
                );
              })}
              <span className="pointer-events-none absolute bottom-1 right-1 text-[9px] tracking-wide text-slate-400">
                A4 · 210 × 297 mm
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              Die Skizze dient nur zur Orientierung. Die PDF-Vorschau zeigt das genaue Ergebnis;
              die automatische Prüfung berücksichtigt die tatsächlichen Textgrößen und alle Deckblattseiten.
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="invoice-layout-block" className="text-sm font-medium text-slate-700">
                Bereich auswählen
              </label>
              <select
                id="invoice-layout-block"
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value as InvoiceBlockId)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal"
                aria-label="Deckblattbereich auswählen"
              >
                {INVOICE_BLOCKS.map((block) => (
                  <option key={block.id} value={block.id}>{block.label}</option>
                ))}
              </select>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3">
              <p className="mb-3 text-sm font-medium text-slate-700">{selectedBlock.label}</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1 text-xs text-slate-600">
                  <span className="block">X-Versatz (mm)</span>
                  <Input
                    type="number"
                    min={MIN_X}
                    max={MAX_X}
                    step="0.1"
                    inputMode="decimal"
                    value={xDraft}
                    onChange={(event) => setXDraft(event.target.value)}
                    onBlur={() => {
                      const parsed = Number(xDraft);
                      updateOffset(selectedId, Number.isFinite(parsed) ? parsed : 0, selectedOffset.y);
                    }}
                    aria-label={`X-Versatz für ${selectedBlock.label} in Millimetern`}
                    className="h-9 tabular-nums"
                  />
                </label>
                <label className="space-y-1 text-xs text-slate-600">
                  <span className="block">Y-Versatz (mm)</span>
                  <Input
                    type="number"
                    min={MIN_Y}
                    max={MAX_Y}
                    step="0.1"
                    inputMode="decimal"
                    value={yDraft}
                    onChange={(event) => setYDraft(event.target.value)}
                    onBlur={() => {
                      const parsed = Number(yDraft);
                      updateOffset(selectedId, selectedOffset.x, Number.isFinite(parsed) ? parsed : 0);
                    }}
                    aria-label={`Y-Versatz für ${selectedBlock.label} in Millimetern`}
                    className="h-9 tabular-nums"
                  />
                </label>
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                Pfeiltasten bewegen um 1 mm, Umschalt + Pfeiltaste um 5 mm.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button type="button" variant="outline" size="sm" onClick={resetSelected}>
                <RotateCcw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                Bereich zurücksetzen
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={resetAll}>
                Alle Bereiche zurücksetzen
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
